import React, { useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useAppContext } from '../../context/AppContext';
import { checkAdminClaims } from '../../utils/adminClaims';
import { logger } from '../../utils/logger';

/**
 * Component that manages synchronization between Firebase Auth and AppContext
 * Handles setting admin status from Firebase custom claims
 */
const AdminRoleManager: React.FC = () => {
  const { currentUser } = useAuth();
  const { setUser, user } = useAppContext();

  useEffect(() => {    
    const syncUserWithAppContext = async () => {
      if (!currentUser) {
        // Only clear if not already null
        if (user !== null) setUser(null);
        return;
      }
      try {
        // Use the utility function to check if user has admin claims
        const isAdmin = await checkAdminClaims(currentUser);
        // Only update if changed
        if (!user || user.id !== currentUser.uid || user.isAdmin !== isAdmin || user.email !== (currentUser.email || '')) {
          // Fetch extended profile for additional information
          const { getUserProfileByUid } = await import('../../services/userService');
          const profile = await getUserProfileByUid(currentUser.uid);
          
          setUser({
            id: currentUser.uid,
            email: currentUser.email || '',
            isAdmin: isAdmin,
            role: profile?.role || 'user',
            membershipStatus: profile?.membershipStatus || 'pending'
          });
        }
        if (isAdmin) {
          logger.info('👑 User admin status synchronized with AppContext');
        }
      } catch (error) {
        logger.error('❌ Error synchronizing user with AppContext:', error);
      }
    };
    syncUserWithAppContext();
  }, [currentUser, setUser, user]);

  // This is a utility component, it doesn't render anything
  return null;
};

export default AdminRoleManager;
